import React, { useState, useMemo } from 'react';
import { MapPin, Users, Plus, Eye, Edit, Trash2, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import type { Itinerary, ItineraryMaintenanceState, ItineraryPaginationInfo } from '../../../../types/itineraryMaintenance';
import { mockItineraries } from '../../../../data/mockItineraries';
import ItineraryDetailsModal from './components/ItineraryDetailsModal';
import '../../../../styles/itineraryMaintenance.css';

interface ItineraryMaintenanceProps {
  className?: string;
  onEditItinerary?: (itinerary: Itinerary) => void;
}

const ItineraryMaintenance: React.FC<ItineraryMaintenanceProps> = ({ className = '', onEditItinerary }) => {
  const [state, setState] = useState<ItineraryMaintenanceState>({
    itineraries: mockItineraries,
    searchTerm: '',
    currentPage: 1,
    itemsPerPage: 5,
    totalItineraries: mockItineraries.length,
    selectedItineraries: [],
    isLoading: false
  });

  const [viewDetailsModal, setViewDetailsModal] = useState<{
    isOpen: boolean;
    itinerary: Itinerary | null;
  }>({ isOpen: false, itinerary: null });

  // Filter itineraries based on search term
  const filteredItineraries = useMemo(() => {
    if (!state.searchTerm) {
      return state.itineraries;
    }

    const searchLower = state.searchTerm.toLowerCase();
    return state.itineraries.filter(itinerary =>
      itinerary.itineraryCode.toLowerCase().includes(searchLower) ||
      itinerary.itineraryName.toLowerCase().includes(searchLower)
    );
  }, [state.itineraries, state.searchTerm]);

  // Calculate pagination
  const paginationInfo = useMemo((): ItineraryPaginationInfo => {
    const totalItineraries = filteredItineraries.length;
    const totalPages = Math.ceil(totalItineraries / state.itemsPerPage);
    const startItinerary = (state.currentPage - 1) * state.itemsPerPage + 1;
    const endItinerary = Math.min(state.currentPage * state.itemsPerPage, totalItineraries);

    return {
      currentPage: state.currentPage,
      totalPages,
      totalItineraries,
      itemsPerPage: state.itemsPerPage,
      startItinerary: totalItineraries > 0 ? startItinerary : 0,
      endItinerary: totalItineraries > 0 ? endItinerary : 0
    };
  }, [filteredItineraries.length, state.currentPage, state.itemsPerPage]);

  // Get current page itineraries
  const currentPageItineraries = useMemo(() => {
    const startIndex = (state.currentPage - 1) * state.itemsPerPage;
    const endIndex = startIndex + state.itemsPerPage;
    return filteredItineraries.slice(startIndex, endIndex);
  }, [filteredItineraries, state.currentPage, state.itemsPerPage]);

  const handleSearchChange = (searchTerm: string) => {
    setState(prev => ({
      ...prev,
      searchTerm,
      currentPage: 1 // Reset to first page when searching
    }));
  };

  const handlePageChange = (page: number) => {
    setState(prev => ({
      ...prev,
      currentPage: page
    }));
  };

  const handleCreateNew = () => {
    alert('Create New Itinerary Modal\n\nThis would open a form with:\n- Itinerary Code input\n- Itinerary Name input\n- Status configuration');
  };

  const handleViewItinerary = (itinerary: Itinerary) => {
    setViewDetailsModal({ isOpen: true, itinerary });
  };

  const handleEditItinerary = (itinerary: Itinerary) => {
    if (onEditItinerary) {
      onEditItinerary(itinerary);
    } else {
      alert(`Edit Itinerary Modal\n\nThis would open an edit form for:\n- Itinerary Code: ${itinerary.itineraryCode}\n- Itinerary Name: ${itinerary.itineraryName}\n- Status modifications`);
    }
  };

  const handleDeleteItinerary = (itinerary: Itinerary) => {
    const confirmDelete = confirm(`Are you sure you want to delete itinerary "${itinerary.itineraryCode} - ${itinerary.itineraryName}"?\n\nThis action cannot be undone.`);
    if (confirmDelete) {
      setState(prev => ({
        ...prev,
        itineraries: prev.itineraries.filter(i => i.id !== itinerary.id)
      }));
      alert(`Itinerary "${itinerary.itineraryCode} - ${itinerary.itineraryName}" has been deleted successfully.`);
    }
  };

  const renderPagination = () => {
    return (
      <div className="itinerary-pagination">
        <div className="pagination-info">
          Showing {paginationInfo.startItinerary} to {paginationInfo.endItinerary} of {paginationInfo.totalItineraries} itineraries
        </div>
        <div className="pagination-controls">
          <button
            className="pagination-btn"
            onClick={() => handlePageChange(paginationInfo.currentPage - 1)}
            disabled={paginationInfo.currentPage === 1}
          >
            <ChevronLeft size={16} />
          </button>
          
          {Array.from({ length: paginationInfo.totalPages }, (_, i) => i + 1).map((page) => (
            <button
              key={page}
              className={`pagination-number ${page === paginationInfo.currentPage ? 'active' : ''}`}
              onClick={() => handlePageChange(page)}
            >
              {page}
            </button>
          ))}
          
          <button
            className="pagination-btn"
            onClick={() => handlePageChange(paginationInfo.currentPage + 1)}
            disabled={paginationInfo.currentPage === paginationInfo.totalPages}
          >
            <ChevronRight size={16} />
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className={`itinerary-maintenance ${className}`}>
      {/* Header */}
      <div className="itinerary-maintenance-header">
        <div className="itinerary-maintenance-header-left">
          <div className="itinerary-maintenance-icon">
            <MapPin size={24} />
          </div>
          <div className="itinerary-maintenance-title-section">
            <h1 className="itinerary-maintenance-title">Manage Itineraries</h1>
            <div className="itinerary-maintenance-context">
              Manage and oversee travel itinerary records and activities
            </div>
          </div>
        </div>
        
        <div className="itinerary-maintenance-header-actions">
          <button
            className="btn-create-new-itinerary"
            onClick={handleCreateNew}
          >
            <Plus size={16} />
            Create New
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="itinerary-maintenance-search-section">
        <div className="itinerary-search-controls">
          <div className="itinerary-search-input-container">
            <Search size={16} className="itinerary-search-icon" />
            <input
              type="text"
              placeholder="Search by Itinerary name or Itinerary code..."
              value={state.searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="itinerary-search-input"
            />
            <div className="itinerary-search-tooltip">
              Search by Itinerary name or Itinerary code
            </div>
          </div>
        </div>
        
        <div className="itinerary-count-info">
          <Users size={16} className="itinerary-count-icon" />
          <span className="itinerary-count-text">{filteredItineraries.length} itineraries found</span>
        </div>
      </div>

      {/* Table */}
      <div className="itinerary-table-container">
        <table className="itinerary-table">
          <thead className="itinerary-table-header">
            <tr>
              <th className="itinerary-code-column">ITINERARY CODE</th>
              <th className="itinerary-name-column">ITINERARY NAME</th>
              <th className="actions-column">ACTIONS</th>
            </tr>
          </thead>
          <tbody className="itinerary-table-body">
            {currentPageItineraries.map((itinerary) => (
              <tr key={itinerary.id} className="itinerary-row">
                <td className="itinerary-code-cell">{itinerary.itineraryCode}</td>
                <td className="itinerary-name-cell">{itinerary.itineraryName}</td>
                <td>
                  <div className="itinerary-action-buttons">
                    <button
                      className="itinerary-action-btn view"
                      onClick={() => handleViewItinerary(itinerary)}
                      title="View Itinerary"
                    >
                      <Eye size={14} />
                    </button>
                    <button
                      className="itinerary-action-btn edit"
                      onClick={() => handleEditItinerary(itinerary)}
                      title="Edit Itinerary"
                    >
                      <Edit size={14} />
                    </button>
                    <button
                      className="itinerary-action-btn delete"
                      onClick={() => handleDeleteItinerary(itinerary)}
                      title="Delete Itinerary"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {renderPagination()}

      {/* Itinerary Details Modal */}
      <ItineraryDetailsModal
        isOpen={viewDetailsModal.isOpen}
        onClose={() => setViewDetailsModal({ isOpen: false, itinerary: null })}
        itinerary={viewDetailsModal.itinerary}
        onEdit={(itinerary: Itinerary) => {
          setViewDetailsModal({ isOpen: false, itinerary: null });
          handleEditItinerary(itinerary);
        }}
      />
    </div>
  );
};

export default ItineraryMaintenance;